import axios, { AxiosResponse } from "axios";
import sessionGuardian from "@/services/session.guardian";

const backendUrl = process.env.VUE_APP_BACKEND_URL;

class Api {
  constructor() {
    this._setupAxios();
  }

  deletePage(pageId: string): Promise<AxiosResponse> {
    return axios.delete(`/pages/${pageId}`);
  }

  getPage(CustomUrl: string): Promise<AxiosResponse> {
    return axios.get(`/pages/${CustomUrl}`);
  }

  getPages(
    filter: Record<string, any> = {},
    projection: Record<string, any> = {}
  ): Promise<AxiosResponse> {
    const params = new URLSearchParams();
    params.append('filter', btoa(JSON.stringify(filter)));
    params.append('projection', btoa(JSON.stringify(projection)));
    return axios.get('/pages', { params });
  }

  login(credentials: Record<string, any>): Promise<AxiosResponse> {
    return axios.post('/login', credentials);
  }

  saveContact(contact: Record<string, any>): Promise<AxiosResponse> {
    return axios.post('/contacts', contact);
  }

  saveNewsletter(newsletter: Record<string, any>): Promise<AxiosResponse> {
    return axios.post('/newsletter', newsletter);
  }

  savePage(page: Record<string, any>): Promise<AxiosResponse> {
    if (page._id) {
      return axios.put(`/pages/${page._id}`, page);
    } else {
      return axios.post('/pages', page);
    }
  }

  subscribe(subscription: Record<string, any>): Promise<AxiosResponse> {
    return axios.post('/subscriptions', subscription);
  }

  // Buscar config da home
  getHomeConfig(type: string, lang?: string): Promise<AxiosResponse> {
    const url = lang ? `/homes/${type}?lang=${lang}` : `/homes/${type}`;
    return axios.get(url);
  }

  getNewsletters(): Promise<AxiosResponse> {
    return axios.get('/newsletter');
  }

  getContacts(): Promise<AxiosResponse> {
    return axios.get('/contacts');
  }

  // Salvar config da home
  saveHomeConfig(type: string, config: Record<string, any>): Promise<AxiosResponse> {
    return axios.post(`/homes/${type}`, config);
  }

  // ===== MÉTODOS DE ANALYTICS =====

  // Rastrear visita/evento
  trackAnalytics(trackData: Record<string, any>): Promise<AxiosResponse> {
    return axios.post('/analytics/track', trackData);
  }

  // Dashboard completo
  getAnalyticsDashboard(days: number = 7): Promise<AxiosResponse> {
    return axios.get(`/analytics/dashboard?days=${days}`);
  }

  getAnalyticsVisitors(days: number = 7): Promise<AxiosResponse> {
    return axios.get(`/analytics/visitors/${days}`);
  }

  getAnalyticsDevices(): Promise<AxiosResponse> {
    return axios.get('/analytics/devices');
  }

  // Estatísticas geográficas
  getAnalyticsGeo(): Promise<AxiosResponse> {
    return axios.get('/analytics/geo');
  }

  // Estatísticas de páginas
  getAnalyticsPages(): Promise<AxiosResponse> {
    return axios.get('/analytics/pages');
  }

  // Estatísticas de performance
  getAnalyticsPerformance(): Promise<AxiosResponse> {
    return axios.get('/analytics/performance');
  }

  // Limpeza de dados antigos
  cleanupAnalytics(days: number = 90): Promise<AxiosResponse> {
    return axios.delete(`/analytics/cleanup?days=${days}`);
  }

  // ===== MÉTODOS DE USUÁRIOS =====
  getUsers(): Promise<AxiosResponse> {
    return axios.get('/users');
  }

  saveUser(user: Record<string, any>): Promise<AxiosResponse> {
    if (user._id) {
      return axios.put(`/users/${user._id}`, user);
    } else {
      return axios.post('/users', user);
    }
  }

  deleteUser(userId: string): Promise<AxiosResponse> {
    return axios.delete(`/users/${userId}`);
  }

  _setupAxios(): void {
    axios.defaults.baseURL = backendUrl;
    axios.defaults.timeout = 60000; // 60 segundos de timeout para uploads pesados

    axios.interceptors.request.use((config) => {
      config.headers.Authorization = sessionGuardian.getToken() ?? 'unknown';
      return config;
    });
  }
}

export default new Api();
